import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { AuthUser, AuthState } from '../types/index.ts';
import apiClient from '../api/client.ts';
import SessionWarningModal from '../components/SessionWarningModal.tsx';

const INACTIVITY_TIMEOUT_MS = 14400000; // 4 hours
const WARNING_BEFORE_MS = 300000;       // 5 minutes

interface AuthContextValue extends AuthState {
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  switchFacility: (facilityId: number, facilityName: string) => void;
  // Active org (admin can switch orgs client-side without re-login)
  activeOrgId: number;
  activeOrgName: string;
  switchOrg: (orgId: number, orgName: string) => void;
  sessionWarning: boolean;
  extendSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    accessToken: localStorage.getItem('accessToken'),
    refreshToken: localStorage.getItem('refreshToken'),
    isLoading: true,
  });

  // Active org state for admin org-switching (separate from JWT org)
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningTimerRef    = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resetTimersRef     = useRef<() => void>(() => {});

  const [sessionWarning, setSessionWarning] = useState(false);

  const [activeOrg, setActiveOrg] = useState<{ id: number; name: string } | null>(() => {
    const savedId = localStorage.getItem('activeOrgId');
    const savedName = localStorage.getItem('activeOrgName');
    if (savedId && savedName) {
      const id = parseInt(savedId, 10);
      return isNaN(id) ? null : { id, name: savedName };
    }
    return null;
  });

  const refreshUser = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setState((s) => ({ ...s, isLoading: false }));
        return;
      }

      const res = await apiClient.get('/auth/me');
      setState((s) => ({ ...s, user: res.data.data, isLoading: false }));
    } catch {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      setState({ user: null, accessToken: null, refreshToken: null, isLoading: false });
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const login = async (username: string, password: string) => {
    const res = await apiClient.post('/auth/login', { username, password });
    const { accessToken, refreshToken, user } = res.data.data;

    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);

    setState({ user, accessToken, refreshToken, isLoading: false });
    // Reset active org on login to JWT org
    setActiveOrg(null);
    localStorage.removeItem('activeOrgId');
    localStorage.removeItem('activeOrgName');
  };

  const logout = () => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    setSessionWarning(false);
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('activeOrgId');
    localStorage.removeItem('activeOrgName');
    setState({ user: null, accessToken: null, refreshToken: null, isLoading: false });
    setActiveOrg(null);
  };

  const switchFacility = (facilityId: number, facilityName: string) => {
    setState((s) => s.user ? { ...s, user: { ...s.user, facilityId, facilityName } } : s);
  };

  const switchOrg = (orgId: number, orgName: string) => {
    setActiveOrg({ id: orgId, name: orgName });
    localStorage.setItem('activeOrgId', String(orgId));
    localStorage.setItem('activeOrgName', orgName);
  };

  // Inactivity timer — auto-logout after 4 hours; warning at 3h 55min
  const isLoggedIn = !!state.user;
  useEffect(() => {
    if (!isLoggedIn) return;

    const handleActivity = () => {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      if (warningTimerRef.current)    clearTimeout(warningTimerRef.current);

      setSessionWarning(false);

      // Warning fires 5 minutes before logout
      warningTimerRef.current = setTimeout(() => {
        setSessionWarning(true);
      }, INACTIVITY_TIMEOUT_MS - WARNING_BEFORE_MS);

      // Logout fires at 4 hours
      inactivityTimerRef.current = setTimeout(() => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('activeOrgId');
        localStorage.removeItem('activeOrgName');
        setState({ user: null, accessToken: null, refreshToken: null, isLoading: false });
        setActiveOrg(null);
        window.location.href = '/login?expired=1';
      }, INACTIVITY_TIMEOUT_MS);
    };

    resetTimersRef.current = handleActivity;

    const events = ['mousemove', 'keydown', 'mousedown', 'touchstart'] as const;
    events.forEach((e) => document.addEventListener(e, handleActivity, { passive: true }));
    handleActivity(); // start timers immediately on login

    return () => {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      if (warningTimerRef.current)    clearTimeout(warningTimerRef.current);
      events.forEach((e) => document.removeEventListener(e, handleActivity));
    };
  }, [isLoggedIn]);

  const extendSession = useCallback(async () => {
    const rt = localStorage.getItem('refreshToken');
    if (!rt) {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('activeOrgId');
      localStorage.removeItem('activeOrgName');
      setState({ user: null, accessToken: null, refreshToken: null, isLoading: false });
      setActiveOrg(null);
      window.location.href = '/login?expired=1';
      return;
    }
    try {
      const res = await apiClient.post('/auth/refresh', { refreshToken: rt });
      const { accessToken, refreshToken } = res.data.data;
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      // Reset both timers — same as user activity
      resetTimersRef.current();
    } catch {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('activeOrgId');
      localStorage.removeItem('activeOrgName');
      setState({ user: null, accessToken: null, refreshToken: null, isLoading: false });
      setActiveOrg(null);
      window.location.href = '/login?expired=1';
    }
  }, []);

  // Full expired logout — called by SessionWarningModal at countdown = 0
  const handleSessionExpire = useCallback(() => {
    if (inactivityTimerRef.current) { clearTimeout(inactivityTimerRef.current); inactivityTimerRef.current = null; }
    if (warningTimerRef.current)    { clearTimeout(warningTimerRef.current);    warningTimerRef.current    = null; }
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('activeOrgId');
    localStorage.removeItem('activeOrgName');
    setState({ user: null, accessToken: null, refreshToken: null, isLoading: false });
    setActiveOrg(null);
    window.location.href = '/login?expired=1';
  }, []);

  // activeOrgId/activeOrgName: use override if set, else JWT values
  const activeOrgId = activeOrg?.id ?? state.user?.orgId ?? 1;
  const activeOrgName = activeOrg?.name ?? state.user?.orgName ?? '';

  return (
    <AuthContext.Provider value={{
      ...state,
      login,
      logout,
      refreshUser,
      switchFacility,
      activeOrgId,
      activeOrgName,
      switchOrg,
      sessionWarning,
      extendSession,
    }}>
      {children}
      {sessionWarning && (
        <SessionWarningModal
          onExtend={extendSession}
          onLogout={logout}
          onExpire={handleSessionExpire}
        />
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export default AuthContext;
